import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from '../ToolRenderers';

// Message interface for the ServiceSummariesToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Service summary data structure
interface ServiceSummary {
    displayName: string;
    serviceName: string;
    numAnomalies: number;
    numAlerts: number;
    serviceP50Nanos: number;
    serviceP95Nanos: number;
    serviceP99Nanos: number;
    service500s: number;
    service400s: number;
    requestCount: number;
}

interface ServiceSummariesResponse {
    serviceSummaries: ServiceSummary[];
}

// Format nanoseconds to milliseconds with 2 decimal places
const formatLatency = (nanos: number): string => {
    return (nanos / 1000000).toFixed(2) + ' ms';
}

// Tool renderer for service summaries data
export function ServiceSummariesToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // We only want to render the response, not the call
    if (!message.tool_call_response_content) {
        // This is a tool call, do the default rendering
        return <DefaultToolCallRenderer message={message} />;
    }

    let response: ServiceSummariesResponse | null = null;
    try {
        response = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing service summaries response data:', error);
        return null;
    }

    // Validate response
    if (!response || !response.serviceSummaries || !Array.isArray(response.serviceSummaries)) {
        return null;
    }

    // Define columns for the DataTable
    const columns: ColumnDef<ServiceSummary>[] = [
        {
            id: "displayName",
            accessorKey: "displayName",
            header: "Service",
            cell: ({ row }) => <div className="font-medium">{row.original.displayName}</div>,
        },
        {
            id: "requestCount",
            accessorKey: "requestCount",
            header: "Requests",
            cell: ({ row }) => row.original.requestCount.toString(),
        },
        {
            id: "serviceP50Nanos",
            accessorKey: "serviceP50Nanos",
            header: "P50 Latency",
            cell: ({ row }) => formatLatency(row.original.serviceP50Nanos),
        },
        {
            id: "serviceP95Nanos",
            accessorKey: "serviceP95Nanos",
            header: "P95 Latency",
            cell: ({ row }) => formatLatency(row.original.serviceP95Nanos),
        },
        {
            id: "serviceP99Nanos",
            accessorKey: "serviceP99Nanos",
            header: "P99 Latency",
            cell: ({ row }) => formatLatency(row.original.serviceP99Nanos),
        },
        {
            id: "service400s",
            accessorKey: "service400s",
            header: "4XX Errors",
            cell: ({ row }) => row.original.service400s.toString(),
        },
        {
            id: "service500s",
            accessorKey: "service500s",
            header: "5XX Errors",
            cell: ({ row }) => row.original.service500s.toString(),
        },
        {
            id: "numAlerts",
            accessorKey: "numAlerts",
            header: "Alerts",
            cell: ({ row }) => row.original.numAlerts.toString(),
        },
        {
            id: "numAnomalies",
            accessorKey: "numAnomalies",
            header: "Anomalies",
            cell: ({ row }) => row.original.numAnomalies.toString(),
        },
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    return (
        <div className="w-full border rounded">
            <DataTable
                columns={columns}
                data={response.serviceSummaries}
                tableTitle="Service Summaries"
                filters={filters}
                showPagination={true}
                removeBorder={true}
            />
        </div>
    );
}

// Static method to determine if this renderer should render a message
ServiceSummariesToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};